import {
  type BusinessRole,
  type SalesUser,
  canAccessCustomerApplications,
  canAccessCustomerManagement,
  canAccessReviewCenter,
  getBusinessRole,
} from '@/lib/business-workflow'
import { hasFormAdminAccess, hasSuperAdminAccess } from '@/lib/permissions'
import {
  type SystemFeatureKey,
  type SystemFeatureVisibility,
  readSystemFeatureVisibility,
} from '@/lib/system-feature-visibility'
import type { SessionUser } from '@/types/api'

export type DashboardNavGroupId =
  | 'workspace'
  | 'forms'
  | 'customers'
  | 'erp'
  | 'admin'
  | 'integrations'
  | 'system'

export type DashboardNavItemId =
  | 'dashboard'
  | 'forms'
  | 'formTemplates'
  | 'questionGroups'
  | 'customerApplications'
  | 'reviewCenter'
  | 'customers'
  | 'reviewNotifications'
  | 'erpCustomerFields'
  | 'admins'
  | 'sales'
  | 'settings'
  | 'integrations'
  | 'systemNavigation'
  | 'systemShare'
  | 'systemForms'
  | 'systemCustomers'
  | 'systemRuntime'

export type DashboardNavItem = {
  id: DashboardNavItemId
  label: string
  description?: string
  to: string
  requiresAdmin?: boolean
  requiresSales?: boolean
  requiresReviewer?: boolean
  requiresCustomerManagement?: boolean
  requiresSuperAdmin?: boolean
}

export type DashboardNavGroup = {
  id: DashboardNavGroupId
  label: string
  description: string
  items: DashboardNavItem[]
}

export const dashboardRailWidth = 64
export const dashboardSidebarDefaultWidth = 248
export const dashboardSidebarMinWidth = 208
export const dashboardSidebarMaxWidth = 360
export const defaultDashboardNavGroupId: DashboardNavGroupId = 'forms'

export const dashboardNavGroups: DashboardNavGroup[] = [
  {
    id: 'workspace',
    label: '工作台',
    description: '今日摘要與營運狀態',
    items: [
      {
        id: 'dashboard',
        label: '儀表板',
        description: '總覽問卷、回應與最近活動',
        to: '/dashboard',
      },
    ],
  },
  {
    id: 'forms',
    label: '問卷',
    description: '建立、管理與分享問卷',
    items: [
      {
        id: 'forms',
        label: '我的問卷',
        description: '資料夾、卡片、分享與預覽',
        to: '/dashboard/forms',
      },
      {
        id: 'formTemplates',
        label: '問卷範本',
        description: '固定範本與可編輯問卷範本',
        to: '/dashboard/form-templates',
        requiresAdmin: true,
      },
      {
        id: 'questionGroups',
        label: '題組管理',
        description: '可重用題組與題組問題',
        to: '/dashboard/question-groups',
        requiresAdmin: true,
      },
    ],
  },
  {
    id: 'erp',
    label: 'ERP',
    description: 'ERP 建檔欄位與對應設定',
    items: [
      {
        id: 'erpCustomerFields',
        label: '客戶欄位',
        description: '管理問卷題目對應客戶主檔欄位',
        to: '/dashboard/erp/customer-fields',
        requiresAdmin: true,
      },
    ],
  },
  {
    id: 'customers',
    label: '客戶',
    description: '新客戶申請、審核與客戶資料',
    items: [
      {
        id: 'customerApplications',
        label: '新客戶申請',
        description: '業務送出的客戶建檔問卷',
        to: '/dashboard/customer-applications',
        requiresSales: true,
      },
      {
        id: 'reviewCenter',
        label: '審核中心',
        description: '主管審核所有新客戶申請',
        to: '/dashboard/reviews',
        requiresReviewer: true,
      },
      {
        id: 'customers',
        label: '客戶管理',
        description: '客戶狀態、公司資料與問卷來源',
        to: '/dashboard/customers',
        requiresCustomerManagement: true,
      },
      {
        id: 'reviewNotifications',
        label: '審核通知管理',
        description: '未成功發送的審核通知與手動重送',
        to: '/dashboard/review-notifications',
        requiresAdmin: true,
      },
    ],
  },
  {
    id: 'admin',
    label: '管理',
    description: '權限、團隊與系統設定',
    items: [
      {
        id: 'admins',
        label: '管理員',
        description: 'Email 權限與管理範圍',
        to: '/dashboard/admins',
        requiresAdmin: true,
      },
      {
        id: 'sales',
        label: '業務',
        description: '業務與主管關係設定',
        to: '/dashboard/sales',
        requiresAdmin: true,
      },
      {
        id: 'settings',
        label: '設定',
        description: '工作區與一般設定',
        to: '/dashboard/settings',
        requiresSuperAdmin: true,
      },
    ],
  },
  {
    id: 'integrations',
    label: '串接',
    description: 'Webhook 與外部服務串接管理',
    items: [
      {
        id: 'integrations',
        label: '串接管理',
        description: '管理 Webhook 啟用狀態、傳入參數與 n8n 回應格式',
        to: '/dashboard/integrations',
        requiresSuperAdmin: true,
      },
    ],
  },
  {
    id: 'system',
    label: '系統',
    description: '主功能顯示與 mock-safe 系統控制',
    items: [
      {
        id: 'systemNavigation',
        label: '左側功能圖示',
        description: '設定 dashboard 左側功能入口顯示',
        to: '/dashboard/system/navigation',
        requiresSuperAdmin: true,
      },
      {
        id: 'systemShare',
        label: '問卷分享',
        description: '設定分享對話框內子功能顯示',
        to: '/dashboard/system/share',
        requiresSuperAdmin: true,
      },
      {
        id: 'systemForms',
        label: '問卷管理',
        description: '問卷管理功能區塊顯示設定',
        to: '/dashboard/system/forms',
        requiresSuperAdmin: true,
      },
      {
        id: 'systemCustomers',
        label: '客戶流程',
        description: '客戶流程功能區塊顯示設定',
        to: '/dashboard/system/customers',
        requiresSuperAdmin: true,
      },
      {
        id: 'systemRuntime',
        label: '系統狀態',
        description: 'mock-safe runtime/provider 狀態',
        to: '/dashboard/system/runtime',
        requiresSuperAdmin: true,
      },
    ],
  },
]

const customerOnlyBusinessRoles: BusinessRole[] = ['manager', 'sales', 'assistant']

const customerWorkflowDashboardPaths = [
  '/dashboard/customer-applications',
  '/dashboard/customers',
] as const

const reviewCenterDashboardPaths = ['/dashboard/reviews', '/dashboard/review-center'] as const

const dashboardNavItemFeatureKeys: Partial<Record<DashboardNavItemId, SystemFeatureKey>> = {
  forms: 'nav.forms',
  formTemplates: 'forms.templates',
  questionGroups: 'nav.questionGroups',
  customerApplications: 'nav.customerApplications',
  reviewCenter: 'nav.reviewCenter',
  customers: 'nav.customers',
  reviewNotifications: 'nav.reviewNotifications',
  erpCustomerFields: 'nav.erpCustomerFields',
  admins: 'nav.admins',
  sales: 'nav.sales',
  settings: 'nav.settings',
  integrations: 'nav.integrations',
}

export function clampDashboardSidebarWidth(width: number) {
  return Math.min(dashboardSidebarMaxWidth, Math.max(dashboardSidebarMinWidth, Math.round(width)))
}

export function getDashboardMainWidth({
  collapsed,
  sidebarWidth,
}: {
  collapsed: boolean
  sidebarWidth: number
}) {
  if (collapsed) return `calc(100dvw - ${dashboardRailWidth}px)`
  return `calc(100dvw - ${dashboardRailWidth + clampDashboardSidebarWidth(sidebarWidth)}px)`
}

export function itemActive(pathname: string, item: DashboardNavItem) {
  return item.to === '/dashboard'
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(`${item.to}/`)
}

export function isCustomerOnlyDashboardUser(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  salesUsers?: SalesUser[],
) {
  return customerOnlyBusinessRoles.includes(getBusinessRole(user, salesUsers))
}

function isCustomerWorkflowDashboardPath(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/'
  return customerWorkflowDashboardPaths.some(
    (path) => normalizedPathname === path || normalizedPathname.startsWith(`${path}/`),
  )
}

function isReviewCenterDashboardPath(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/'
  return reviewCenterDashboardPaths.some(
    (path) => normalizedPathname === path || normalizedPathname.startsWith(`${path}/`),
  )
}

export function canAccessDashboardPath(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  pathname: string,
  salesUsers?: SalesUser[],
) {
  if (!user) return false
  if (!isCustomerOnlyDashboardUser(user, salesUsers)) return true
  if (isCustomerWorkflowDashboardPath(pathname)) return true
  if (isReviewCenterDashboardPath(pathname)) return canAccessReviewCenter(user, salesUsers)
  return false
}

export function getDashboardDefaultPath(
  user: Pick<SessionUser, 'email' | 'role'> | null | undefined,
  salesUsers?: SalesUser[],
) {
  if (!user) return '/login'
  if (hasSuperAdminAccess(user) || user.role === 'owner') return '/dashboard'

  const businessRole = getBusinessRole(user, salesUsers)
  if (businessRole === 'manager') return '/dashboard/reviews'
  if (businessRole === 'sales' || businessRole === 'assistant') {
    return '/dashboard/customer-applications'
  }

  if (businessRole === 'admin' || hasFormAdminAccess(user)) return '/dashboard/forms'
  return '/dashboard/forms'
}

function itemVisible(
  item: DashboardNavItem,
  user: SessionUser | null | undefined,
  visibility: SystemFeatureVisibility,
) {
  const featureKey = dashboardNavItemFeatureKeys[item.id]
  if (featureKey && !visibility[featureKey]) return false
  if (item.requiresAdmin) return hasFormAdminAccess(user)
  if (item.requiresSales) return canAccessCustomerApplications(user)
  if (item.requiresReviewer) return canAccessReviewCenter(user)
  if (item.requiresCustomerManagement) return canAccessCustomerManagement(user)
  if (item.requiresSuperAdmin) return hasSuperAdminAccess(user)
  return true
}

export function getVisibleDashboardNavGroups(
  user: SessionUser | null | undefined,
  visibility = readSystemFeatureVisibility(),
) {
  const customerOnly = isCustomerOnlyDashboardUser(user)

  return dashboardNavGroups
    .filter((group) => !customerOnly || group.id === 'customers')
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => itemVisible(item, user, visibility)),
    }))
    .filter((group) => group.items.length > 0)
}
